import { useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { axisGridColor, ticksColor } from './color';
import { externalTooltipHandler } from './createCustomTooltip';

const debouncedCreateExternalTooltip = debounce(
  (c, title, tooltipId, isMobile, originalLabels2) =>
    externalTooltipHandler(c, title, tooltipId, isMobile, originalLabels2),
  50,
);

export const useChartModel = ({
  labels,
  data: dataset,
  secondData,
  originalLabels2 = [],
  title,
  color,
  secondColor,
  colorFill,
  secondColorFill,
  isMobile,
}) => {
  const [tooltipId] = useState(uuidv4());

  let lbls = [...labels];
  let ds1 = [...dataset];
  let ds2 = [...secondData];
  let oL2 = [...originalLabels2];

  if (lbls.length === 1 && ds1.length === 1) {
    lbls.push(lbls[0]);
    ds1.push(ds1[0]);
  }

  if (lbls.length === 2 && ds2.length === 1) {
    ds2.push(ds2[0]);
    if (oL2.length === 1) oL2.push(oL2[0]);
  }

  if (labels.length === 2 && secondData.length === 1) {
    // это для того, чтобы показывалась линия на графике, когда у нас одна точка
    secondData.push(secondData[0]);
  }

  const options = useMemo(
    () => ({
      plugins: {
        legend: { display: false, labels: { usePointStyle: true, pointStyle: 'circle' } },
        datalabels: { display: false },
        tooltip: {
          enabled: false,
          external: c => {
            debouncedCreateExternalTooltip(c, title, tooltipId, isMobile, oL2);
          },
          callbacks: {
            labelColor: ctx => ({
              borderColor: ctx.dataset.borderColor,
              backgroundColor: ctx.dataset.borderColor,
            }),
          },
        },
      },
      scales: {
        x: { grid: { borderColor: axisGridColor, borderWidth: 3, color: ticksColor }, ticks: { color: ticksColor } },
        y: { grid: { borderColor: axisGridColor, borderWidth: 3, color: ticksColor }, ticks: { color: ticksColor } },
      },
      responsive: true,
      aspectRatio: isMobile ? 1 : 2,
    }),
    [tooltipId, title, isMobile, oL2],
  );

  const data = useMemo(
    () => ({
      labels: lbls,
      datasets: [
        {
          type: 'line',
          data: ds1,
          borderColor: color || '#FFFFFF80',
          borderWidth: 1,
          backgroundColor: colorFill || 'rgba(70, 21, 92, 1)',
          fill: true,
          pointLabels: false,
          pointBackgroundColor: '#682984',
          pointBorderColor: color || '#A64DFF',
          pointRadius: 5,
          pointHoverRadius: 6,
        },
        {
          type: 'line',
          data: ds2,
          borderColor: secondColor || '#FFFFFF80',
          borderWidth: 1,
          backgroundColor: secondColorFill || 'rgba(70, 21, 92, 0.6)',
          fill: true,
          pointBackgroundColor: '#285E3D',
          pointBorderColor: secondColor || '#228949',
          pointRadius: 5,
          pointHoverRadius: 6,
          pointLabels: false,
        },
      ],
    }),
    [lbls, ds1, ds2, color, secondColor, colorFill, secondColorFill],
  );

  return { data, labels: lbls, options };
};
